import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook personalizado para manejar la navegación basada en roles
 */
export const useNavigation = () => {
  const { user, selectedCompany } = useAuth();

  // Función para verificar si el usuario es Supervisor
  const isSupervisor = useMemo(() => {
    if (!user) return false;
    
    // Verificar por nombre de usuario si contiene "Supervisor"
    const isSupervisorByName = user.name && user.name.toLowerCase().includes('supervisor');
    
    if (!user.roles) {
      return isSupervisorByName;
    }
    
    // Verificar si tiene el rol de Supervisor
    // Los roles pueden venir como array de objetos o array de strings
    const isSupervisorByRole = user.roles.some(role => {
      if (typeof role === 'string') {
        return role === 'Supervisor';
      } else if (typeof role === 'object' && role.name) {
        return role.name === 'Supervisor';
      }
      return false;
    });
    
    return isSupervisorByRole || isSupervisorByName;
  }, [user]);

  // Función para verificar si puede acceder a una ruta específica
  const canAccessRoute = useMemo(() => {
    return (path) => {
      // Si es Supervisor, solo puede acceder a Tareos y Autorizaciones
      if (isSupervisor) {
        const allowedPaths = ['/tareos', '/autorizaciones'];
        return allowedPaths.includes(path);
      }
      
      // Todos los demás roles pueden acceder a todas las rutas
      return true;
    };
  }, [isSupervisor]);

  // Lista de elementos de navegación filtrados según el rol
  const filteredNavItems = useMemo(() => {
    const allNavItems = [
      { 
        path: '/', 
        label: 'Panel principal', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v14l-5-3-5 3V5z" />
          </svg>
        )
      },
      { 
        path: '/empleados', 
        label: 'Empleados', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        )
      },
      { 
        path: '/tareos', 
        label: 'Tareos', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      { 
        path: '/autorizaciones', 
        label: 'Autorizaciones', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      { 
        path: '/planillas', 
        label: 'Planillas', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      },
      { 
        path: selectedCompany?.id ? `/calculos/${selectedCompany.id}` : '/calculos', 
        label: 'Cálculos', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )
      },
      { 
        path: '/reportes', 
        label: 'Reportes', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        )
      },
      { 
        path: '/configuracion', 
        label: 'Configuración', 
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )
      }
    ];

    // Si es Supervisor, filtrar solo las rutas permitidas
    if (isSupervisor) {
      return allNavItems.filter(item => canAccessRoute(item.path));
    }

    // Para todos los demás roles, mostrar todos los elementos
    return allNavItems;
  }, [isSupervisor, canAccessRoute, selectedCompany?.id]);

  // Función para obtener la ruta de redirección por defecto según el rol
  const getDefaultRoute = useMemo(() => {
    if (isSupervisor) {
      return '/tareos'; // Los supervisores van a Tareos por defecto
    }
    return '/'; // Todos los demás van al Dashboard
  }, [isSupervisor]);

  return {
    isSupervisor,
    canAccessRoute,
    filteredNavItems,
    getDefaultRoute,
    user
  };
};